import React, { FC } from 'react';

import { DetailsList } from '@eika/details-list';
import { IconTypes } from '@eika/icon';
import { WarningLabel } from '@eika/label';
import { Link } from '@eika/link';

import { useConfig } from '../../defaultConfig';
import type { AccountId, AccountInterest, LoanDetails } from '../../generated-types';
import useTranslation, { format } from '../../hooks/useTranslation';
import getLoanInterest from '../../utils/mapper/getLoanInterest';
import { Content } from '../shared/Content';
import EffectiveInterestInfo from './EffectiveInterestInfo';

interface Props extends React.PropsWithChildren {
    interests?: AccountInterest;
    accountId?: AccountId;
    loanDetails?: LoanDetails;
    isLoading: boolean;
    hasAccount: boolean;
}

const LoanDetailsOwnersContainer: FC<Props> = ({
    children,
    interests,
    loanDetails,
    accountId,
    isLoading,
    hasAccount = true,
}) => {
    const translated = useTranslation();
    const interest = getLoanInterest(interests);
    const {
        loanRepaymentPlanLink,
        featureToggles: { enableLoanRepaymentPlan },
    } = useConfig();

    const targetInterest = loanDetails?.interests
        ? {
              nominal: loanDetails.interests.nominalInterests,
              total: loanDetails.interests.totalInterests,
          }
        : { total: interest?.rate };

    const loanNotFound = !isLoading && !hasAccount && !!accountId;

    return (
        <Content>
            <div className="loan-details-owners">
                <DetailsList
                    title={loanNotFound ? undefined : translated.loanDetails.loanOwners}
                    className="loan-details-owners__list"
                    isLoading={isLoading}
                    skeletonCount={2}
                >
                    {loanNotFound ? (
                        <h2 className="loan-details-owners__list-not-found">{translated.loanDetails.noLoan}</h2>
                    ) : (
                        children
                    )}
                </DetailsList>
            </div>
            {targetInterest && (
                <div className="loan-details-interest-container">
                    <div className="loan-details-interest">
                        {targetInterest.nominal ? (
                            <>
                                <span className="loan-details-interest__label">
                                    {translated.loanDetails.nominalInterst}
                                </span>
                                <span className="loan-details-interest__value">{targetInterest.nominal} %</span>
                                <span className="loan-details-interest__label--small">
                                    {format(translated.loanDetails.effectiveInterest, targetInterest.total)}
                                    <EffectiveInterestInfo />
                                </span>
                            </>
                        ) : (
                            <>
                                <div>{translated.loanDetails.interest}</div>
                                <div className="loan-details-interest__value">{targetInterest.total} %</div>
                            </>
                        )}
                    </div>

                    {!!loanRepaymentPlanLink && enableLoanRepaymentPlan && (
                        <Link
                            className="loan-details__replayment-link"
                            href={`${loanRepaymentPlanLink.url}?id=${accountId?.value}`}
                            icon={IconTypes.KONTOER_24}
                        >
                            {translated.loanRepaymentPlan.viewRepaymentPlan}
                        </Link>
                    )}
                </div>
            )}
            {!loanRepaymentPlanLink?.url && (
                <WarningLabel
                    className="loan-details-payment-plan-message"
                    text={translated.loanDetails.paymentPlanMessage}
                />
            )}
        </Content>
    );
};

export default LoanDetailsOwnersContainer;
